<template>
  <div class="wrapper">
    <notification-message class="notificationMessage" />

    <!-- Start spinner when load all data -->
    <div class="spinner" v-if="endLoading">
      <v-progress-circular
        :rotate="-90"
        :size="100"
        :width="15"
        :value="spinnerValue"
        color="red"
      >
        {{ spinnerValue }}
      </v-progress-circular>
    </div>
    <!-- End spinner when load all data -->

    <div class="grid-container" v-else>
      <main-filters class="main-filters neumorphism-out" />

      <div class="calendarSelection">
        <!-- Start time direction arrows -->
        <div class="gridTimeDirection">
          <table class="selectTimeDirection" style="width: 100%">
            <tr class="flex">
              <td class="previousTime">
                <v-btn
                  rounded
                  class="neumorphism-in"
                  text
                  @click="previousTimeDirection"
                >
                  <v-icon color="red darken-1" class="mr-2"
                    >mdi-skip-previous-circle</v-icon
                  >
                  {{ previousDate }}
                </v-btn>
              </td>
              <td>
                <span class="currentTime">
                  {{ currentDate | dateFormat }}
                </span>
              </td>
              <td class="nextTime">
                <v-btn
                  text
                  @click="nextTimeDirection"
                  rounded
                  class="neumorphism-in"
                >
                  {{ nextDate }}
                  <v-icon color="red darken-1" class="ml-2"
                    >mdi-skip-next-circle</v-icon
                  >
                </v-btn>
              </td>
            </tr>
          </table>
          <!-- End time direction arrows -->
        </div>

        <!-- Start import from DG2 -->
        <div class="add-event-btn" v-if="editMode" @click="onClick">
          <ExportImportDG2></ExportImportDG2>
        </div>
        <!-- End import from DG2 -->

        <!-- Start edit mode option -->
        <div>
          <v-switch
            v-model="editMode"
            color="red"
            :label="$t('swithMod.label')"
            :disabled="disabledSwith"
            v-if="isAdmin"
          ></v-switch>
        </div>
        <!-- End edit mode option -->

        <!-- Start date selection for the calendar -->
        <div class="timeSelector">
          <div class="bordered">
            <v-btn
              rounded
              class="neumorphism-out-animated"
              icon
              @click="goToToday"
              :aria-label="$t('timeDirection.btnToday')"
            >
              <v-icon color="red darken-1">mdi-calendar-star</v-icon>
            </v-btn>
            <span>{{ $t("timeDirection.today") }}</span>
          </div>
          <div>
            <span>{{ $t("timeDirection.selectDate") }}</span>
            <v-btn
              @click="openPickerTime"
              icon
              rounded
              class="neumorphism-out-animated"
              :aria-label="$t('timeDirection.btnSelectDate')"
            >
              <v-icon color="red darken-1">mdi-calendar-search</v-icon>

              <template v-if="showPickerTime">
                <v-row class="pickerTimeDirection" justify="center">
                  <v-date-picker
                    v-model="picker"
                    :locale="this.$i18n.locale"
                    color="red darken-1"
                    show-adjacent-months
                    :landscape="true"
                    @change="goToTime"
                    v-click-outside="closePickerTimeDirection"
                  >
                  </v-date-picker>
                </v-row>
              </template>
            </v-btn>
          </div>
        </div>
        <!-- End date selection for the calendar -->

        <!-- Start selection calendar view (eg week / month / year) -->
        <div>
          <v-select
            :items="times"
            v-model="timePeriod"
            item-text="name"
            item-value="id"
          >
          </v-select>
        </div>

        <!--Sync Last Import Info-->
        <div class="lastsyncinfo">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                @click="goToLastSync(lastSync.id)"
                :color="getIconColor()"
                v-on="on"
                >mdi-cloud-download</v-icon
              >
            </template>
            <span v-if="lastSync.id != null">
              {{ $t("lastSync.date") + dateLastSync }}
              {{ $t("lastSync.time") + timeLastSync }}</span
            >
            <span v-else>
              {{ $t("lastSync.noImport") }}
            </span>
          </v-tooltip>
        </div>
      </div>
      <!--End Sync Last Import Info-->
      <!-- End selection calendar view (eg week / month / year) -->

      <!-- Start main calendar -->
      <div class="calendar">
        <div class="outer-grid neumorphism-in">
          <div class="inner-grid">
            <div class="blank-area">
              <div class="add-event">
                <div class="add-event-btn" v-if="editMode">
                  <v-btn
                    id="add-btn-event"
                    icon
                    class="neumorphism-out-animated"
                    @click="adding = !adding"
                    :disabled="selectedOpen || adding || !editMode"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  <span class="text"> {{ $t("addEventCard.title") }} </span>
                </div>
                <div class="add-event-btn">
                  <v-btn
                    icon
                    class="neumorphism-out-animated"
                    @click="openEditTask(selectedTask)"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  <span class="text"> {{ $t("addTaskCard.btn") }} </span>
                </div>
                <!-- Start download cvs memento -->
                <excel-memento></excel-memento>
                <!-- End download cvs memento -->
              </div>
            </div>

            <div
              id="usersScroller"
              class="grid-users-container"
              ref="usersGrid"
              @scroll="usersGridScroll()"
            >
              <user-row
                v-for="(user, index) in paginatedUsers"
                :key="index"
                :user="user"
                @show-user-hitoric="showUserHitoric($event)"
              />
            </div>
          </div>

          <div id="scroller" class="inner-grid">
            <div class="grid-tl-container">
              <span
                class="grid-header grid-month-name neumorphism-out"
                v-for="month in months"
                :key="month.i"
                :style="month.gridCol"
              >
                {{ month.text | capitalize }}
              </span>
              <span
                class="grid-header week-cell neumorphism-out"
                v-for="(week, index) in weeks"
                :key="`${week.i} ${index}`"
                :style="week.gridCol"
              >
                {{ week.text }}
              </span>
              <span
                class="grid-header day-cell neumorphism-out"
                :class="{
                  weekend: dayName.isWeekEndDay,
                  holiday: dayName.isHoliday,
                  datePassed: dayName.isPassed,
                  isCurrentDay: dayName.isCurrentDay,
                }"
                v-for="dayName in dayNames"
                :key="dayName.i"
              >
                {{ dayName.text }}
              </span>
              <span
                class="grid-header date-cell neumorphism-out"
                :class="{
                  weekend: date.isWeekEndDay,
                  holiday: date.isHoliday,
                  datePassed: date.isPassed,
                  isCurrentDay: date.isCurrentDay,
                }"
                v-for="date in dates"
                :id="date.i"
                :key="date.i"
              >
                {{ date.text }}
              </span>
            </div>
            <div
              class="grid-scheduler-container"
              ref="eventsGrid"
              @scroll="calendarGridScroll()"
            >
              <div
                v-if="paginatedUsers.length === 0"
                class="mt-16 px-10"
                style="position: absolute"
              >
                <pre class="text-h5">{{
                  $t("gridScheduler.noUserDisplay", [calendarType])
                }}</pre>
              </div>
              <div
                class="grid-scheduler-row neumorphism-out"
                v-for="(user, y) in paginatedUsers"
                :key="`${y}.${user.id}`"
                @mouseup="endResize($event)"
                @mousemove="startResize($event)"
              >
                <span
                  class="body-cell drop-zone"
                  v-for="x in dates"
                  :class="{
                    weekend: x.isWeekEndDay,
                    holiday: x.isHoliday,
                    datePassed: x.isPassed,
                  }"
                  :style="{ 'z-index': isDragging ? '1' : '' }"
                  :key="x.i"
                  :id="`${x.i};${y}`"
                  @drop="onDrop($event, x.i, y)"
                  @dragover.prevent="dragOver($event)"
                  @dragleave.prevent="dragLeave()"
                  @dragend.prevent="dragEnd($event)"
                >
                  <grid-leave-period
                    v-if="!cellIsEmptyLeave(x.i, user.id)"
                    :leavePeriod="
                      leavePeriods.find(
                        (lp) => lp.x === x.i && lp.employeeId === user.id
                      )
                    "
                    :draggable="false"
                    :resizable="false"
                    :columnWidth="columnWidth"
                    :unityColumnWidth="unityColumnWidth"
                    @show-leave-period="showLeavePeriod"
                  />

                  <grid-task
                    v-if="!cellIsEmptyTask(x.i, user.id)"
                    :task="
                      tasks.find((t) => t.x === x.i && t.userId === user.id)
                    "
                    :draggable="editMode && !resizer.resizing && !x.isPassed"
                    :resizable="true"
                    @start-drag="startDrag"
                    @init-resize="initResize"
                    @delete-event="deleteMyEvent"
                    :columnWidth="columnWidth"
                    :unityColumnWidth="unityColumnWidth"
                    @show-task="showTask"
                  />

                  <grid-intervention-followed
                    v-if="!cellIsEmptyIFollowed(x.i, user.id)"
                    :iFollowed="
                      interventionsFollowed.find(
                        (e) => e.x === x.i && e.details.userDto.id === user.id
                      )
                    "
                    @show-event="showInterventionFollowed"
                    @delete-event="deleteMyEvent"
                    :columnWidth="columnWidth"
                    :unityColumnWidth="unityColumnWidth"
                  />
                  <grid-event
                    v-if="!cellIsEmpty(x.i, user.id)"
                    :event="
                      displayedEvents.find(
                        (e) => e.x === x.i && e.details.user.id === user.id
                      )
                    "
                    :draggable="editMode && !resizer.resizing && !x.isPassed"
                    @start-drag="startDrag"
                    @init-resize="initResize"
                    @show-event="showEvent"
                    @delete-event="deleteMyEvent"
                    :resizable="editMode && !x.isPassed"
                    :columnWidth="columnWidth"
                    :unityColumnWidth="unityColumnWidth"
                  />
                </span>
              </div>
            </div>
          </div>

          <v-btn
            class="btn-close-info"
            elevation="10"
            fab
            small
            @click="closeEvent"
            v-if="
              selectedOpen ||
              adding ||
              leavePeriodOpen ||
              taskOpen ||
              iFollowedOpen
            "
          >
            <v-icon>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <div class="info-card" v-if="selectedOpen && !adding">
          <event-info-card
            :selected="true"
            :event="selectedEvent"
            :locations="locations"
            :users="users"
            :editMode="editMode"
            @close="closeEvent"
            @eventEdit="openEditEvent"
            @openCreate="openCreateInterFollow"
            @taskEdit="openEditTask"
          />
        </div>
        <div class="info-card" v-if="leavePeriodOpen && !adding">
          <leave-period-info
            :event="selectedEvent"
            :users="users.fin"
            :alone="true"
          ></leave-period-info>
        </div>
        <div class="info-card" v-if="taskOpen && !adding">
          <task-info
            :task="selectedTask"
            :alone="true"
            :editMode="editMode"
            @openEditTaskDialog="openEditTask(selectedTask)"
            @closeCard="taskOpen = false"
          ></task-info>
        </div>
        <div class="info-card" v-if="adding">
          <add-event-card @close="closeEvent" />
        </div>

        <div class="info-card" v-if="iFollowedOpen && !adding">
          <intervention-followed-info
            :interFollowed="selectedInterFollowed"
            :alone="true"
            :editMode="editMode"
            @closeCard="iFollowedOpen = false"
          />
          <!-- TODO Add openEditInterFollowDialog on selectedInterFollowed-->
        </div>
      </div>
      <!-- End main calendar -->

      <!-- Start selection type of a user -->
      <v-tabs hide-slider show-arrows id="tab" color="red" left>
        <div class="tabs">
          <v-tab
            active-class="neumorphism-in"
            v-for="calendar in $enumUserType"
            :key="calendar.value"
            @click="getCalendarSelected(calendar.value)"
          >
            {{ calendar.text }}
          </v-tab>
        </div>

        <v-tab-item
          vertical
          v-for="calendar in $enumUserType"
          :key="calendar.value"
        >
        </v-tab-item>
      </v-tabs>
      <!-- End selection type of a user -->
    </div>

    <v-dialog v-model="dropDialogError" width="500">
      <v-card>
        <v-card-title>
          {{ $t("errorDialog.title") }}
        </v-card-title>

        <v-card-text> {{ currentDropError }} </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" @click:outside="resteEditForm" width="500">
      <v-card>
        <add-event-card
          ref="addEventCard"
          :editedEvent="editedEvent"
          @close="editDialog = false"
        >
        </add-event-card>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="createInterFollow"
      @click:outside="resetInterFollowAdd"
      width="500"
    >
      <v-card>
        <add-intervention-followed-card
          ref="addInterFollowCard"
          :interventionToFollow="editedEvent"
          @close="createInterFollow = false"
        >
        </add-intervention-followed-card>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="editTaskDialog"
      @click:outside="resetEditFormTask"
      width="500"
    >
      <v-card>
        <add-task-card
          ref="addTaskCard"
          :editedTask="editedTask"
          @close="editTaskDialog = false"
        >
        </add-task-card>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="userHitoricDialog.show"
      width="500"
      @click:outside="resteUserHitoric()"
    >
      <dialog-user-animated-folowed-traynings
        ref="dialogUserAnimatedFolowedTraynings"
        :userIdDg2="userHitoricDialog.userIdDg2"
      ></dialog-user-animated-folowed-traynings>
    </v-dialog>
  </div>
</template>

<script>
import { DateTime, Interval } from "luxon";
import { mapActions, mapGetters } from "vuex";
import GridEvent from "@/components/components-planning/public/calendar/grid-scheduler/grid/GridEvent";
import GridLeavePeriod from "@/components/components-planning/public/calendar/grid-scheduler/grid/GridLeavePeriod";
import GridTask from "@/components/components-planning/public/calendar/grid-scheduler/grid/GridTask";
import EventInfoCard from "@/components/components-planning/public/calendar/grid-scheduler/event-details/EventInfoCard";
import AddEventCard from "@/components/components-planning/public/calendar/grid-scheduler/add-event/AddEventCard.vue";
import MainFilters from "@/components/components-planning/public/calendar/filters/MainFilters.vue";
import ExcelMemento from "@/components/components-planning/public/calendar/grid-scheduler/export/ExcelMemento.vue";
import ExportImportDG2 from "@/components/components-planning/public/calendar/grid-scheduler/export/ExportImportDG2.vue";
import NotificationMessage from "@/components/components-base/notification/NotificationMessage.vue";
import UserRow from "@/components/components-planning/public/calendar/grid-scheduler/grid/UserRow.vue";
import CaptionLocation from "@/components/components-planning/public/calendar/filters/CaptionLocation.vue";
import axios from "axios";
import LeavePeriodInfo from "@/components/components-planning/public/calendar/grid-scheduler/event-details/leavePeriodInfo.vue";
import DialogUserAnimatedFolowedTraynings from "@/components/components-planning/public/calendar/grid-scheduler/dialog/DialogUserAnimatedFolowedTraynings.vue";
import TaskInfo from "@/components/components-planning/public/calendar/grid-scheduler/event-details/taskInfo.vue";
import AddTaskCard from "@/components/components-planning/public/calendar/grid-scheduler/add-event/AddTaskCard.vue";
import GridInterventionFollowed from "@/components/components-planning/public/calendar/grid-scheduler/grid/GridInterventionFollowed.vue";
import InterventionFollowedInfo from "@/components/components-planning/public/calendar/grid-scheduler/event-details/InterventionFollowedInfo.vue";
import AddInterventionFollowedCard from "@/components/components-planning/public/calendar/grid-scheduler/add-event/AddInterventionFollowedCard.vue";
import EventModule from "@/store/planning/eventStore";
import InterventionFollowedModule from "@/store/planning/interventionFollowedStore";
import LeavePeriodModule from "@/store/planning/leavePeriodStore";
import FreeDaysModule from "@/store/planning/freeDaysStore";
import { getLastSyncHttp } from "@/services/planning/SyncService.js";
import authService from "@/services/planning/AuthService";

export default {
  props: [],
  name: "grid",
  components: {
    UserRow,
    GridEvent,
    GridLeavePeriod,
    GridTask,
    EventInfoCard,
    MainFilters,
    AddEventCard,
    ExcelMemento,
    ExportImportDG2,
    NotificationMessage,
    CaptionLocation,
    LeavePeriodInfo,
    DialogUserAnimatedFolowedTraynings,
    TaskInfo,
    AddTaskCard,
    GridInterventionFollowed,
    InterventionFollowedInfo,
    AddInterventionFollowedCard,
  },
  data() {
    return {
      lastSync: {},
      dateString: null,
      dateTimeLastSync: null,
      dateLastSync: null,
      timeLastSync: null,
      userHitoricDialog: {
        show: false,
        userIdDg2: null,
      },
      timePeriod: "month",
      editedEvent: null,
      editedTask: null,
      editDialog: false,
      createInterFollow: false,
      editTaskDialog: false,
      adding: false,
      dialog: false,
      root: null,
      isAdmin: false,
      editMode: false,
      disabledSwith: false,
      times: [
        { id: "week", name: this.$t("timeDirection.week") },
        { id: "month", name: this.$t("timeDirection.month") },
        { id: "quarter", name: this.$t("timeDirection.quarter") },
        { id: "year", name: this.$t("timeDirection.year") },
      ],
      dropDialogError: false,
      dropErrors: new Map([
        ["trainingsConflict", this.$t("errorDialog.trainingsConflict")],
        ["interFollowConflict", this.$t("errorDialog.interFollow")],
        ["freeDay", this.$t("errorDialog.freeDay")],
        ["pastDay", this.$t("errorDialog.pastDay")],
        ["leavePeriod", this.$t("errorDialog.leavePeriod")],
        ["holiday", this.$t("errorDialog.holiday")],
      ]),

      currentDropError: null,
      resizer: {
        currentEvent: null,
        parent: null,
        resizing: false,
        unit: 41.6, // 2.6 rem
        halfUnit: 20.8,
        startX: null,
        startWidth: null,
        width: null,
        moduloWidth: null,
      },
      displayedDays: null,
      weekendOffset: null,
      dateStart: null,
      dateEnd: null,
      currentDate: null,
      currentWeek: null,
      currentMonth: null,
      currentYear: null,
      dates: [],
      dayNames: [],
      weeks: [],
      months: [],
      draggedEvent: {
        eventObj: null,
        initialPosition: null,
        preview: {
          obj: null,
          isDropped: false,
        },
      },
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      leavePeriodOpen: false,
      taskOpen: false,
      iFollowedOpen: false,
      picker: new Date().toISOString().substr(0, 10),
      showPickerTime: false,
      previousDate: null,
      nextDate: null,
      showAddEventCard: false,
      currentMonthScrolled: null,
      goToMonthScroll: null,
      spinnerValue: 0,
      endLoading: true,
      getScrollPercentActive: false,
      columnWidth: null,
      unityColumnWidth: null,
      displayedUserCount: 11,
      isDragging: false,
    };
  },
  computed: {
    ...mapGetters("locationModule", { locations: "getLocations" }),
    ...mapGetters("courseModule", { courses: "getCourses" }),
    ...mapGetters("userModule", {
      users: "getUsersForm",
      displayedUsers: "getOnUserDisplay",
      calendarType: "getCalendar",
    }),
    ...mapGetters("leavePeriodModule", { leavePeriods: "getLeavePeriods" }),
    ...mapGetters("eventModule", {
      events: "getEvents",
      displayedEvents: "getOnEventDisplay",
      startingDay: "getStartingDay",
      eventsMultiArray: "getEventsMultiarray",
    }),
    ...mapGetters("freeDaysModule", {
      holidays: "getHolidays",
      weekEndDays: "getWeekEndDays",
    }),
    ...mapGetters("taskModule", {
      tasks: "getTasks",
      selectedTask: "getSelectedTask",
    }),
    ...mapGetters("interventionFollowedModule", {
      interventionsFollowed: "getInterventionsFollowed",
      selectedInterFollowed: "getSelectedInterFollowed",
    }),
    paginatedUsers: function () {
      return this.displayedUsers.slice(0, this.displayedUserCount);
    },
  },
  filters: {
    capitalize: function (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    dateFormat: function (value) {
      if (value) {
        value = new Date(value);
        return value.getFullYear();
      }
    },
  },
  watch: {
    currentMonthScrolled: function () {
      if (this.currentMonthScrolled != new Date().getMonth() + 1) {
        let date = DateTime.fromISO(this.picker)
          .startOf("month")
          .set({ month: this.currentMonthScrolled })
          .toISODate();
        this.loadInterventionsFollowed(date);
        this.loadEvents(date);
        this.loadLeavePeriods(date);
        this.loadTasks(this.picker);
      }
    },
    timePeriod: function () {
      this.clickSelectTime();
    },
  },
  async mounted() {
    this.root = document.documentElement;
    this.getWeekEndDays();
    await this.getHolidays();
    await this.loadNotHiddenLocations();
    this.spinnerValue = 20;
    await this.loadCourses();
    this.spinnerValue = 40;
    await this.loadUsers();
    this.spinnerValue = 60;
    this.columnAndEventWidth();
    this.setStartingDay();
    await this.mainBuildingTimeRows();
    this.spinnerValue = 80;
    this.fetchCalendarType(this.$enumUserType[0].value);
    await this.loadTasks(this.picker);
    await this.loadInterventionsFollowed(this.picker);
    await this.loadEvents(this.picker);
    await this.loadCenters();
    this.spinnerValue = 90;
    this.loadLeavePeriods(this.picker).then(() => {
      this.spinnerValue = 100;
      this.endLoading = false;
      if (this.timePeriod === "year") {
        this.$nextTick(() => this.scrollTimeLine());
        this.$nextTick(() =>
          document
            .getElementById("scroller")
            .addEventListener("scroll", this.getScrollPercent)
        );
      }
      this.$nextTick(() =>
        document
          .getElementById("usersScroller")
          .addEventListener("scroll", this.addUsersToList)
      );
    });
    this.loadEventsMasterCrud();
    this.connectToWebsocket();
    this.convDate(this.dateTimeLastSync);
  },
  created() {
    this.setTimePeriod(this.timePeriod);
    if ($cookies.isKey("config")) {
      let cookiesConfig = $cookies.get("config");
      this.setTimePeriod(cookiesConfig.timePref);
    }

    if (authService.isAuth()) {
      if (!authService.userHasSomeRoles(["ADMINISTRATIF"])) {
        this.editMode = false;
        this.disabledSwith = true;
      } else {
        this.isAdmin = true;
        this.editMode = false;
        this.disabledSwith = false;
      }
    }
    this.getLastSync();
  },
  beforeDestroy() {
    document.removeEventListener("scroll", this.getScrollPercent);
  },
  methods: {
    ...mapActions("eventModule", [
      "loadEvents",
      "editEvent",
      "deleteEvent",
      "fetchStartingDay",
      "updateEventsMultiarray",
      "loadEventsMasterCrud",
      "setTimePeriod",
    ]),
    ...mapActions("leavePeriodModule", ["loadLeavePeriods"]),
    ...mapActions("websocketModule", [
      "connectToWebsocket",
      "logoutFromWebsocket",
    ]),
    ...mapActions("locationModule", ["loadNotHiddenLocations"]),
    ...mapActions("courseModule", ["loadCourses"]),
    ...mapActions("userModule", [
      "loadUsers",
      "fetchCalendarType",
      "updateUsersMultiarray",
    ]),
    ...mapActions("taskModule", ["loadTasks", "setSelectedTask"]),
    ...mapActions("freeDaysModule", ["getWeekEndDays", "getHolidays"]),
    ...mapActions("interventionFollowedModule", [
      "loadInterventionsFollowed",
      "setSelectedInterFollowed",
    ]),
    ...mapActions("centerModule", ["loadCenters"]),
    showUserHitoric(userIdDg2) {
      this.userHitoricDialog.show = true;
      this.userHitoricDialog.userIdDg2 = userIdDg2;
    },
    /**
     *
     */
    resteUserHitoric() {
      this.userHitoricDialog.show = false;
      this.editedEvent = null;
    },
    /**
     *
     */
    resteEditForm() {
      this.editedEvent = null;
      this.$refs.addEventCard.closeEvent();
    },
    resetEditFormTask() {
      this.editedTask = null;
      this.$refs.addTaskCard.closeEvent();
    },
    resetInterFollowAdd() {
      this.$refs.addInterFollowCard.closeEvent();
    },
    openEditEvent(event) {
      this.editDialog = true;
      this.editedEvent = event;
    },
    openCreateInterFollow(event) {
      this.createInterFollow = true;
      this.editedEvent = event;
    },
    openEditTask(task) {
      this.editTaskDialog = true;
      this.editedTask = task;
    },
    /**
     * Open intervention details window selected in grid scheduler.
     *
     * @param {event} event
     */
    showEvent(event) {
      this.selectedEvent = event;
      if (!this.resizer.resizing) {
        this.leavePeriodOpen = false;
        this.taskOpen = false;
        this.iFollowedOpen = false;
        this.selectedOpen = true;
      }
    },
    /**
     * color gestion DG2 icon
     */
    getIconColor() {
      if (this.lastSync.failed) {
        return "error"; // Rouge
      } else if (this.lastSync.id != null) {
        return "success"; // Vert
      } else {
        return "gray"; // Gris
      }
    },
    /**
     * Close intervention details window.
     */
    closeEvent() {
      this.selectedEvent = null;
      this.setSelectedTask(null);
      this.setSelectedInterFollowed(null);
      this.selectedOpen = false;
      this.leavePeriodOpen = false;
      this.taskOpen = false;
      this.iFollowedOpen = false;
      this.adding = false;
    },
    showLeavePeriod(event) {
      this.selectedEvent = event;
      this.selectedOpen = false;
      this.iFollowedOpen = false;
      this.taskOpen = false;
      this.leavePeriodOpen = true;
    },
    showTask(task) {
      this.setSelectedTask(task.details.id);
      this.selectedOpen = false;
      this.leavePeriodOpen = false;
      this.taskOpen = true;
      this.iFollowedOpen = false;
      this.leavePeriodOpen = false;
    },
    showInterventionFollowed(inter) {
      this.setSelectedInterFollowed(inter.details.id);
      this.selectedOpen = false;
      this.leavePeriodOpen = false;
      this.taskOpen = false;
      this.iFollowedOpen = true;
    },
    cellIsEmpty(x, userId) {
      const event = this.displayedEvents.find(
        (event) => event.x === x && event.details.user.id === userId
      );
      return event === undefined ? true : false;
    },
    cellIsEmptyLeave(x, userEmployeeId) {
      const leavePeriod = this.leavePeriods.find(
        (leave) => leave.x === x && leave.employeeId === userEmployeeId
      );
      return leavePeriod === undefined ? true : false;
    },
    cellIsEmptyTask(x, userEmployeeId) {
      const task = this.tasks.find(
        (t) => t.x === x && t.userId === userEmployeeId
      );
      return task === undefined ? true : false;
    },
    cellIsEmptyIFollowed(x, userId) {
      const iFollowed = this.interventionsFollowed.find(
        (iFollo) => iFollo.x === x && iFollo.details.userDto.id === userId
      );
      return iFollowed === undefined ? true : false;
    },
    startDrag(evt, item) {
      this.draggedEvent.eventObj = evt.target;
      this.draggedEvent.initialPosition = evt.target.parent;
      this.draggedEvent.preview.obj = evt.target.cloneNode();
      this.draggedEvent.preview.obj.setAttribute("id", "dropPrev");
      this.draggedEvent.preview.obj.setAttribute("class", "drop-preview");
      this.draggedEvent.preview.obj.innerHtml = "";

      setTimeout(function () {
        evt.target.style.visibility = "hidden";
      }, 1);

      evt.dataTransfer.dropEffect = "move";
      evt.dataTransfer.effectAllowed = "move";
      evt.dataTransfer.setData("itemId", item.id);
    },
    advIntervToIntervDto(event) {
      if (event.details) {
        return {
          eventTitle: event.details.course.title,
          courseId: event.details.course.id,
          courseIdDg2: event.details.course.idDg2,
          comment: event.details.comment,
          locationId: event.details.location.id,
          locationIdDg2: event.details.location.idDg2,
          userId: event.details.user.id,
          dateStart: event.details.dateStart,
          dateEnd: event.details.dateEnd,
          timeStart: event.details.timeStart,
          timeEnd: event.details.timeEnd,
          validated: event.details.validated,
          type: event.details.type,
          version: event.details.version,
          id: event.details.id,
          slug: event.details.slug,
          idDg2: event.details.idDg2,
          attendeesCount: event.details.attendeesCount,
        };
      } else {
        return {
          eventTitle: event.course.title,
          courseId: event.course.id,
          courseIdDg2: event.course.idDg2,
          comment: event.comment,
          locationId: event.location.id,
          locationIdDg2: event.location.idDg2,
          userId: event.user.id,
          dateStart: event.dateStart,
          dateEnd: event.dateEnd,
          timeStart: event.timeStart,
          timeEnd: event.timeEnd,
          validated: event.validated,
          type: event.type,
          version: event.version,
          id: event.id,
          slug: event.slug,
          idDg2: event.idDg2,
          attendeesCount: event.attendeesCount,
        };
      }
    },
    onDrop(evt, x, y) {
      this.isDragging = false;

      let itemId = evt.dataTransfer.getData("itemId");

      let user = this.users[y];

      let currentDate = this.formatDate(new Date());

      let startDateTime = new DateTime.fromISO(x);

      this.currentDropError = "";

      if (itemId.includes("INTER")) {
        let event = this.events.find((event) => event.id == itemId);

        let endDate = startDateTime
          .plus({ days: event.duration - 1 })
          .toISODate();

        // x is DateStart
        if (currentDate >= x) {
          this.currentDropError = this.dropErrors.get("pastDay");
        }

        if (
          FreeDaysModule.methods.isHoliDay(x) ||
          FreeDaysModule.methods.isHoliDay(endDate)
        ) {
          this.currentDropError = this.dropErrors.get("holiday");
        }

        if (
          FreeDaysModule.methods.isWeekEndDay(x) ||
          FreeDaysModule.methods.isWeekEndDay(endDate)
        ) {
          this.currentDropError = this.dropErrors.get("freeDay");
        }

        if (!EventModule.methods.intervalIsEmpty(x, event, user)) {
          this.currentDropError = this.dropErrors.get("trainingsConflict");
        }

        if (
          !InterventionFollowedModule.methods.intervalIsEmptyInterventionFollowed(
            x,
            event,
            user
          )
        ) {
          this.currentDropError = this.dropErrors.get("interFollowConflict");
        }

        if (
          !LeavePeriodModule.methods.intervalIsEmptyLeavePeriod(x, event, user)
        ) {
          this.currentDropError = this.dropErrors.get("leavePeriod");
        }

        let interFollowToCheck = this.interventionsFollowed.filter(
          (inter) => inter.details.interventionId === event.details.id
        );

        interFollowToCheck.forEach((element) => {
          let userFollowing = this.users.find(
            (u) => u.id === element.details.userId
          );
          this.currentDropError +=
            InterventionFollowedModule.methods.checkIfInterventionFollowedOverlapsWithOtherEvents(
              userFollowing,
              x,
              endDate,
              element
            );
        });

        if (this.currentDropError != "") {
          this.dropDialogError = true;
        } else {
          if (document.getElementById("dropPrev")) {
            document.getElementById("dropPrev").remove();
          }

          let itemId = evt.dataTransfer.getData("itemId");
          let event = this.events.find((event) => event.id == itemId);

          if (event.x != x) {
            event.details.dateStart = x;
            let start = new DateTime.fromISO(x);
            event.details.dateEnd = start
              .plus({ days: event.duration - 1 })
              .toISODate();
            event.x = x;
          }

          if (event.y != y) {
            event.y = y;
            event.details.user = user;
          }

          this.editEvent(this.advIntervToIntervDto(event.details));

          if (this.selectedOpen) {
            this.showEvent(event);
          }

          // this.selectedEvent = this.events.find((event) => event.id == itemId);

          this.draggedEvent.eventObj.style.visibility = "";
          this.draggedEvent.preview.obj = null;
          this.draggedEvent.preview.isDropped = false;
          this.draggedEvent.eventObj = null;
        }
      }

      if (itemId.includes("TASK")) {
        if (currentDate <= x) {
          if (document.getElementById("dropPrev")) {
            document.getElementById("dropPrev").remove();
          }

          let itemId = evt.dataTransfer.getData("itemId");
          let task = this.tasks.find((task) => task.id == itemId);

          if (task.x != x) {
            task.details.dateStart = x;
            let start = new DateTime.fromISO(x);
            task.details.dateEnd = start
              .plus({ days: task.duration - 1 })
              .toISODate();
            task.x = x;
          }

          if (task.y != y) {
            task.y = y;
            task.details.user = user;
          }

          // this.editEvent(this.advIntervToIntervDto(event.details));

          if (this.selectedOpen) {
            this.showEvent(task);
          }

          // this.selectedEvent = this.events.find((event) => event.id == itemId);

          this.draggedEvent.eventObj.style.visibility = "";
          this.draggedEvent.preview.obj = null;
          this.draggedEvent.preview.isDropped = false;
          this.draggedEvent.eventObj = null;
        } else {
          this.currentDropError = this.dropErrors.get("pastDay");
          this.dropDialogError = true;
        }
      }
    },

    dragOver(evt) {
      if (
        evt.target.children.length === 0 &&
        !this.draggedEvent.preview.isDropped
      ) {
        this.draggedEvent.preview.isDropped = false;
        try {
          evt.target.appendChild(this.draggedEvent.preview.obj);
        } catch (error) {}
      }
    },
    dragLeave() {
      this.isDragging = true;
      this.draggedEvent.preview.isDropped = false;
    },
    dragEnd() {
      // Method when move event outside the grid
      if (document.getElementById("dropPrev")) {
        document.getElementById("dropPrev").remove();
      }
      if (this.draggedEvent.eventObj != null)
        this.draggedEvent.eventObj.style.visibility = "";
      this.draggedEvent.preview.obj = null;
      this.draggedEvent.eventObj = null;
      this.draggedEvent.preview.isDropped = false;
      this.isDragging = false;
    },
    /**
     * Initialize values for the resizing of an intervention.
     *
     * @param {$event} e it a mousedown event emitted in GridEvent.vue component
     */
    initResize(e) {
      this.resizer.resizing = true;
      this.resizer.startX = e.clientX;
      this.resizer.parent = e.target.parentElement;

      this.resizer.currentEvent = this.events.find(
        (event) => event.id === this.resizer.parent.id
      );

      this.resizer.startWidth = parseInt(
        document.defaultView.getComputedStyle(this.resizer.parent).width,
        10
      );
      this.resizer.parent.style.opacity = 0.5;
    },
    /**
     * Watch the movement of the mouse to modify the width of the intervention being resizing
     *
     * @param {$event} e is a mousemove event emitted in GridEvent.vue component
     */
    startResize(e) {
      if (this.resizer.resizing) {
        this.resizer.width =
          this.resizer.startWidth + e.clientX - this.resizer.startX;

        this.resizer.moduloWidth = this.resizer.width % this.resizer.unit;

        if (
          this.resizer.moduloWidth > 0 &&
          this.resizer.moduloWidth < this.resizer.halfUnit
        ) {
          this.resizer.width =
            this.resizer.width - this.resizer.moduloWidth + this.resizer.unit;
        } else if (this.resizer.moduloWidth > this.resizer.halfUnit) {
          this.resizer.width =
            this.resizer.width - this.resizer.moduloWidth + this.resizer.unit;
        }

        this.resizer.parent.style.width = this.resizer.width - 2 + "px";
      }
    },
    /**
     * Calcul and apply the new duration of the intervention resized
     *
     * @param {$event} e is a mouseup event emitted in GridEvent.vue component
     */
    endResize(e) {
      if (this.resizer.resizing) {
        let currentEvent = this.resizer.currentEvent;

        let initialDuration = currentEvent.duration;

        const user = this.users.find(
          (u) => u.id === currentEvent.details.user.id
        );

        if (this.resizer.width < this.resizer.unit) {
          this.resizer.width = this.resizer.startWidth;
        }

        let durationTemp = parseInt(this.resizer.width / this.resizer.unit);

        // Calcul the new duration
        currentEvent.duration = durationTemp < 1 ? 1 : durationTemp;

        let testEnd = new DateTime.fromISO(currentEvent.details.dateStart)
          .plus({ day: currentEvent.duration - 1 })
          .toISODate();

        // set error to empty value
        this.currentDropError = "";

        // Check if the new interval is empty and endDate is not a weekEnd and if there is week end days in the new Interval

        if (FreeDaysModule.methods.isHoliDay(testEnd)) {
          this.currentDropError = this.dropErrors.get("holiday");
        }

        if (FreeDaysModule.methods.isWeekEndDay(testEnd)) {
          this.currentDropError = this.dropErrors.get("freeDay");
        }

        if (
          !EventModule.methods.intervalIsEmpty(
            currentEvent.x,
            currentEvent,
            user
          )
        ) {
          this.currentDropError = this.dropErrors.get("trainingsConflict");
        }

        if (
          !InterventionFollowedModule.methods.intervalIsEmptyInterventionFollowed(
            currentEvent.x,
            currentEvent,
            user
          )
        ) {
          this.currentDropError = this.dropErrors.get("interFollowConflict");
        }

        if (
          !LeavePeriodModule.methods.intervalIsEmptyLeavePeriod(
            currentEvent.x,
            currentEvent,
            user
          )
        ) {
          this.currentDropError = this.dropErrors.get("leavePeriod");
        }

        let interFollowToCheck = this.interventionsFollowed.filter(
          (inter) => inter.details.interventionId === currentEvent.details.id
        );

        interFollowToCheck.forEach((element) => {
          let userFollowing = this.users.find(
            (u) => u.id === element.details.userId
          );
          this.currentDropError +=
            InterventionFollowedModule.methods.checkIfInterventionFollowedOverlapsWithOtherEvents(
              userFollowing,
              currentEvent.x,
              testEnd,
              element
            );
        });

        if (this.currentDropError != "") {
          currentEvent.duration = initialDuration;
          this.resizer.parent.style.width = this.resizer.startWidth + "px";
          this.dropDialogError = true;
        } else {
          if (currentEvent.duration != initialDuration) {
            let start = new DateTime.fromISO(currentEvent.details.dateStart);
            let endDate = start
              .plus({ day: currentEvent.duration - 1 })
              .toISODate();
            currentEvent.details.dateEnd = endDate;
            // Apply the new duration
            this.editEvent(this.advIntervToIntervDto(currentEvent.details));
          }

          currentEvent.details.duration = currentEvent.duration;
          this.selectedEvent = currentEvent;
          this.resizer.resizing = false;
          this.resizer.parent.style.opacity = 1;
        }
      }
    },
    /**
     * Delete selected intervention
     *
     * @param {$event} e is a click event emitted in GridEvent.vue component
     */
    deleteMyEvent(e) {
      this.deleteEvent(e.details);
    },
    /**
     * Set the day when the calendar has to start
     *
     * @param {string} startDay day when will start the calendar
     */
    setStartingDay(startDay) {
      let day = new DateTime.local().setLocale(this.$i18n.locale);
      if (startDay != null)
        day = new DateTime.fromISO(startDay).setLocale(this.$i18n.locale);
      this.fetchStartingDay(day.startOf(this.timePeriod));
      this.currentDate = this.startingDay;
    },
    /**
     * Set the number of column depending of the view selected (eg week, month, year, quarter)
     */
    setColumnCount() {
      this.dateStart = this.startingDay.startOf("week");
      switch (this.timePeriod) {
        case "week":
          this.dateEnd = this.dateStart.plus({ days: 6 });
          this.displayedDays = 7;
          this.root.style.setProperty("--column-width", "var(--column-week)");

          break;
        case "month":
          const daysToAdd = 7 - this.startingDay.endOf("month").weekday;
          this.dateEnd = this.startingDay
            .endOf("month")
            .plus({ days: daysToAdd });
          this.displayedDays = Math.round(
            new Interval.fromDateTimes(this.dateStart, this.dateEnd).length(
              "days"
            )
          );
          this.fetchStartingDay(this.startingDay.startOf("week"));
          this.root.style.setProperty(
            "--column-width",
            "var(--column-default)"
          );

          break;
        case "year":
          this.dateStart = this.startingDay.startOf("year");
          this.displayedDays = this.dateStart.daysInYear;
          this.root.style.setProperty(
            "--column-width",
            "var(--column-default)"
          );
          break;
        case "quarter":
          const daysToAdd2 = 7 - this.startingDay.endOf("quarter").weekday;
          this.dateEnd = this.startingDay
            .endOf("quarter")
            .plus({ days: daysToAdd2 });
          this.displayedDays = Math.round(
            new Interval.fromDateTimes(this.dateStart, this.dateEnd).length(
              "days"
            )
          );
          this.fetchStartingDay(this.startingDay.startOf("week"));
          this.root.style.setProperty(
            "--column-width",
            "var(--column-default)"
          );

          break;
      }
    },
    /**
     * Create the array "dates" containing the date of the day (eg 1, 16, 30)
     */
    buildDateItem(day, x) {
      const dateNbr = {
        x: x,
        y: 3,
        w: 1,
        h: 1,
        i: day.toISODate(),
        type: "dateNbr",
        text: day.day,
        static: true,
        isWeekEndDay: false,
        isHoliday: false,
        isPassed: false,
        isCurrentDay: false,
      };
      if (this.weekEndDays.includes(dateNbr.i)) {
        dateNbr.isWeekEndDay = true;
      }
      if (this.holidays.includes(dateNbr.i)) {
        dateNbr.isHoliday = true;
      }
      let now = this.formatDate(new Date());
      if (
        dateNbr.i < now &&
        !this.weekEndDays.includes(dateNbr.i) &&
        !this.holidays.includes(dateNbr.i)
      ) {
        dateNbr.isPassed = true;
      }
      if (dateNbr.i == now) dateNbr.isCurrentDay = true;
      this.dates.push(dateNbr);
    },
    /**
     * Create the array "dayNames" containing the letter of the day (eg M for Monday)
     */
    buildDayItem(day, x) {
      const dayName = {
        x: x,
        y: 2,
        w: 1,
        h: 1,
        type: "dayName",
        text: day.weekdayLong.charAt(0).toUpperCase(),
        i: `dN${day.weekdayLong.charAt(0).toUpperCase()}${x}.${2}`,
        static: true,
        isWeekEndDay: false,
        isHoliday: false,
        isPassed: false,
        isCurrentDay: false,
      };
      if (this.weekEndDays.includes(day.toISODate())) {
        dayName.isWeekEndDay = true;
      }
      if (this.holidays.includes(day.toISODate())) {
        dayName.isHoliday = true;
      }
      let now = this.formatDate(new Date());
      if (
        day.toISODate() < now &&
        !this.weekEndDays.includes(day.toISODate()) &&
        !this.holidays.includes(day.toISODate())
      ) {
        dayName.isPassed = true;
      }
      if (day.toISODate() == now) dayName.isCurrentDay = true;
      this.dayNames.push(dayName);
    },
    /**
     * Create the array "weeks" containing weeks number (eg W49 for one week present in December)
     */
    buildWeekItem(day, x) {
      const week = {
        x: x,
        y: 1,
        w: 7,
        h: 1,
        type: "weekNumber",
        text: this.$t("timeDirection.shortWeek") + `${day.weekNumber}`,
        i: `wN${day.weekNumber}-${day.year}`,
        static: true,
        gridCol: {
          gridColumnStart: x + 1,
          gridColumnEnd: x + 8,
        },
      };
      this.weeks.push(week);
    },
    /**
     * Create the array "months" containing every month (eg December - 2021)
     */
    buildMonthItem(day, daysToEndOfMonth, x) {
      let month = {
        x: x,
        y: 0,
        w: daysToEndOfMonth,
        h: 1,
        type: "monthName",
        text: `${day.monthLong} - ${day.year} (${day.quarter})`, //Luxon > short name month.
        i: `mN${day.month}-${day.year}`,
        static: true,
        gridCol: {
          gridColumnStart: x + 1,
          gridColumnEnd: x + daysToEndOfMonth + 1,
        },
      };
      this.months.push(month);
    },
    /**
     * Create the array "quarters" containing every quarter (eg 2 - 2021)
     */
    buildQuarterItem(day, daysToEndOfQuarter, x) {
      let quarter = {
        x: x,
        y: 0,
        w: daysToEndOfQuarter,
        h: 1,
        type: "quarterName",
        text: `${day.quarter} - ${day.year}`, //Luxon > short name month.
        i: `qN${day.quarter}-${day.year}`,
        static: true,
        gridCol: {
          gridColumnStart: x + 1,
          gridColumnEnd: x + daysToEndOfQuarter + 1,
        },
      };
      this.quarters.push(quarter);
    },
    getDaysToEndOfQuarter(day) {
      let daysToEndOfQuarter = Math.round(
        new Interval.fromDateTimes(day, day.endOf("quarter")).length("days")
      );
      //Manage when a week is between two months for example
      if (this.timePeriod === "week" && daysToEndOfQuarter > 7) {
        daysToEndOfQuarter = 7;
      } else if (
        this.timePeriod === "quarter" &&
        day.endOf("quarter") > this.dateEnd
      ) {
        daysToEndOfQuarter = Math.round(
          new Interval.fromDateTimes(day, day.endOf("week")).length("days")
        );
      }
      return daysToEndOfQuarter;
    },
    getDaysToEndOfMonth(day) {
      let daysToEndOfMonth = Math.round(
        new Interval.fromDateTimes(day, day.endOf("month")).length("days")
      );
      //Manage when a week is between two months for example
      if (this.timePeriod === "week" && daysToEndOfMonth > 7) {
        daysToEndOfMonth = 7;
      } else if (
        this.timePeriod === "month" &&
        day.endOf("month") > this.dateEnd
      ) {
        daysToEndOfMonth = Math.round(
          new Interval.fromDateTimes(day, day.endOf("week")).length("days")
        );
      }
      return daysToEndOfMonth;
    },
    /**
     * Build the calendar lines in the planning
     */
    buildTimelineRows() {
      let day = this.dateStart;
      let currMonth = null;
      let currQuarter = null;
      let currWeek = null;
      let nbDay = null;

      this.dates = [];
      this.dayNames = [];
      this.weeks = [];
      this.months = [];
      this.quarters = [];

      // Number of days x Number of users
      // For each line, nbcases = nbdays
      if (this.timePeriod === "year") {
        this.weekendOffset = Math.round(
          // Nb of days between current X and the first Saturday or Sunday
          // Gap between dateStart and the end of the week. Send back Saturday
          new Interval.fromDateTimes(day, day.endOf("week")).length("days") - 1
        );
      } else {
        this.weekendOffset = day.ordinal + 5;
      }

      for (let index = 0; index < this.displayedDays; index++) {
        let posX = index;
        this.buildDateItem(day, day.ordinal);
        this.buildDayItem(day, posX);
        if (currWeek == null || currWeek != day.weekNumber) {
          this.buildWeekItem(day, posX);
          currWeek = day.weekNumber;
        }
        if (currMonth == null || currMonth != day.month) {
          const daysToEndOfMonth = this.getDaysToEndOfMonth(day);
          this.buildMonthItem(day, daysToEndOfMonth, posX);
          currMonth = day.month;
        }
        if (currQuarter == null || currQuarter != day.quarter) {
          const daysToEndOfQuarter = this.getDaysToEndOfQuarter(day);
          this.buildQuarterItem(day, daysToEndOfQuarter, posX);
          currQuarter = day.quarter;
        }
        day = day.plus({ days: 1 });
      }
      this.months[this.months.length - 1].gridCol.gridColumnEnd =
        this.weeks[this.weeks.length - 1].gridCol.gridColumnEnd;
    },
    async mainBuildingTimeRows() {
      this.setColumnCount();
      this.buildTimelineRows();

      switch (this.timePeriod) {
        case "year":
          this.previousDate = this.currentDate.minus({ year: 1 }).year;
          this.nextDate = this.currentDate.plus({ year: 1 }).year;
          break;
        case "quarter":
          this.previousDate = this.currentDate.minus({ quarter: 1 }).quarter;
          this.nextDate = this.currentDate.plus({ quarter: 1 }).quarter;
          break;
        case "month":
          this.previousDate = this.currentDate.minus({ month: 1 }).monthLong;
          this.nextDate = this.currentDate.plus({ month: 1 }).monthLong;
          break;
        case "week":
          this.previousDate = this.currentDate.minus({ week: 1 }).weekNumber;
          this.nextDate = this.currentDate.plus({ week: 1 }).weekNumber;
          break;
      }
    },
    /**
     * With the yearly view, this method will scroll to the date selected by the client
     */
    scrollTimeLine() {
      let element = null;
      if (this.timePeriod === "year") {
        element = document.getElementById(this.picker);
        element.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "start",
        });
        // when start the application we need to arrive directly to today's date (and avoid call to the database for events)
        if (!this.getScrollPercentActive) {
          this.getScrollPercentActive = true;
          element.scrollIntoView({
            block: "end",
            inline: "start",
            onAnimationEnd: () => element.focus(),
          });
        }
      } else if (this.timePeriod === "month") {
        element = document.getElementById(this.picker);
        element.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "start",
        });
      }
    },
    /**
     * This method redo the calendar depending of the selected date with the date picker.
     */
    goToTime() {
      this.setStartingDay(this.picker);
      this.mainBuildingTimeRows().then((e) => {
        // DOM is updated after this.$nextTick function
        this.$nextTick(function () {
          this.scrollTimeLine();
        });
        this.loadEvents(this.picker);
        this.loadLeavePeriods(this.picker);
        this.loadTasks(this.picker);
        this.loadInterventionsFollowed(this.picker);
      });
    },
    /**
     * This method redo the calendar depending of the selected time (year / month / week).
     */
    clickSelectTime() {
      let config = $cookies.get("config");
      config.timePref = this.timePeriod;
      this.setTimePeriod(config.timePref);
      $cookies.set("config", config, "1y");

      this.columnAndEventWidth();

      this.setStartingDay(this.picker);
      this.mainBuildingTimeRows();
      // DOM is updated after this.$nextTick function
      this.$nextTick(function () {
        this.scrollTimeLine();
      });
      this.loadEvents(this.picker);
      this.loadLeavePeriods(this.picker);
      this.loadTasks(this.picker);
      this.loadInterventionsFollowed(this.picker);
    },
    /**
     * Will go in the past. If the yearly view is selected, it will go to last year at the same period.
     * If the montly view is selected it will go to last month.
     * If the weekly viw is selected it will go to last week.
     * It will load events depending of the time selected.
     */
    async previousTimeDirection() {
      let date = null;
      switch (this.timePeriod) {
        case "year":
          date = this.currentDate.minus({ year: 1 });
          this.loadCalendar(date);
          break;
        case "quarter":
          date = this.currentDate.minus({ quarter: 1 });
          this.loadCalendar(date);
          break;
        case "month":
          date = this.currentDate.minus({ month: 1 });
          this.loadCalendar(date);
          break;
        case "week":
          date = this.currentDate.minus({ week: 1 });
          this.loadCalendar(date);
          break;
      }
    },
    async loadCalendar(date) {
      this.setStartingDay(date);
      this.mainBuildingTimeRows();
      await this.loadEvents(date);
      this.loadLeavePeriods(date);
      this.loadTasks(date);
      this.loadInterventionsFollowed(date);
    },
    /**
     * Will go in the future. If the yearly view is selected, it will go to next year at the same period.
     * If the montly view is selected it will go to next month.
     * If the weekly viw is selected it will go to next week.
     * It will load events depending of the time selected.
     */
    async nextTimeDirection() {
      let date = null;

      switch (this.timePeriod) {
        case "year":
          date = this.currentDate.plus({ year: 1 });
          this.loadCalendar(date);
          break;
        case "quarter":
          date = this.currentDate.plus({ quarter: 1 });
          this.loadCalendar(date);
          break;
        case "month":
          date = this.currentDate.plus({ month: 1 });
          this.loadCalendar(date);
          break;
        case "week":
          date = this.currentDate.plus({ week: 1 });
          this.loadCalendar(date);
          break;
      }
    },
    /**
     * Scroll to today's date and load events around today.
     */
    goToToday() {
      this.picker = DateTime.fromMillis(Date.now()).toISODate();
      this.goToTime();
      this.loadEvents(this.picker);
      this.loadLeavePeriods(this.picker);
      this.loadTasks(this.picker);
      this.loadInterventionsFollowed(this.picker);
    },
    closePickerTimeDirection() {
      this.showPickerTime = false;
    },
    openPickerTime() {
      this.showPickerTime = !this.showPickerTime;
    },
    /**
     * Will load all users and events from the new type of calendar selected.
     * Type of calendars existing : ADMIN, TRAINER, SALESPERSON...
     * It will reset filters if some parameters were selected.
     */
    async getCalendarSelected(calendar) {
      this.updateEventsMultiarray({
        city: null,
        option: null,
        validated: null,
      });
      this.updateUsersMultiarray({
        locations: [],
        users: [],
        skills: [],
        companies: [],
      });

      let date = DateTime.fromISO(this.picker)
        .startOf("month")
        .set({ month: this.currentMonthScrolled })
        .toISODate();
      this.loadEvents(date).then((e) => {
        this.events.forEach((e) => {
          e.display = true;
        });
      });

      this.closeEvent();

      this.fetchCalendarType(calendar);

      this.loadLeavePeriods(this.picker);

      this.loadTasks(this.picker);

      this.loadInterventionsFollowed(this.picker);
    },
    /**
     * Load events depending where we are with the scroll in the planning.
     * Method use only for yearly view because there is no scroll for the monthly and weekly ones.
     * ex : currentMonthScrolled => June. The client scroll on the right and arrive in July so goToMonthScroll will be July.
     * currentMonthScrolled will become July as well.
     */
    getScrollPercent() {
      if (this.timePeriod === "year") {
        const scroller = document.getElementById("scroller");
        const sl = "scrollLeft";
        let maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
        let percentScroll = (scroller[sl] * 100) / maxScrollLeft;

        this.goToMonthScroll = Math.floor(percentScroll / 8.5) + 1; // 100/12 = 8.333

        if (this.currentMonthScrolled != this.goToMonthScroll) {
          this.currentMonthScrolled = this.goToMonthScroll;
        }
      }
    },
    addUsersToList() {
      const scroller = document.getElementById("usersScroller");

      if (
        scroller.scrollHeight - scroller.offsetHeight <=
        scroller.scrollTop + 50
      ) {
        this.displayedUserCount += 10;
      }
    },
    usersGridScroll() {
      this.$refs.eventsGrid.scrollTop = this.$refs.usersGrid.scrollTop;
    },
    calendarGridScroll() {
      this.$refs.usersGrid.scrollTop = this.$refs.eventsGrid.scrollTop;
    },
    /**
     * Adapt size of columns (and so event) from the planning depending if it is a weekly or a yearly/monthly view.
     */
    columnAndEventWidth() {
      if (this.timePeriod === "week") {
        this.columnWidth = 9;
        this.unityColumnWidth = "vw";
        let viewportWidth = window.innerWidth; // to determine % with vw
        this.resizer.unit = (this.columnWidth * viewportWidth) / 100;
        this.resizer.halfUnit = this.resizer.unit / 2;
      } else {
        this.columnWidth = 3;
        this.unityColumnWidth = "rem";
        this.resizer.unit = 48;
        this.resizer.halfUnit = 24;
      }
    },
    formatDate(date) {
      return (
        date.getFullYear() +
        "-" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + date.getDate()).slice(-2)
      );
    },
    async getLastSync() {
      await getLastSyncHttp().then((response) => {
        this.lastSync = response.data;
        this.dateTimeLastSync = DateTime.fromISO(this.lastSync.endOfSync);
      });
    },
    async convDate(date) {
      this.timeLastSync = date.toFormat("HH:mm:ss");
      this.dateLastSync = date.toFormat("dd-MM-yyyy");
    },
    /**
     * goToLastSync push for SyncDG2
     */
    goToLastSync(syncid) {
      if (syncid != null && this.isAdmin) {
        this.$router.push({
          name: "ImportDetails",
          params: { id: syncid },
        });
      }
    },
  },
};
</script>

<style lang="scss">
.spinner {
  margin-top: 10rem;
}

#tab > div {
  background-color: rgba(255, 255, 255, 0);
}
#tab {
  border-radius: 25px;
  .v-tabs-bar {
    margin: 20px 0 10px;
    box-shadow: 0 0px 20px 0 rgba(66, 66, 66, 0.308);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);

    .v-tab {
      overflow: hidden;
      border-radius: 20px;
      margin: 0;
    }
  }
}

.calendar {
  display: flex;
  justify-content: space-around;
  height: var(--height-container);

  .btn-close-info {
    z-index: 2;
    margin: auto auto auto -25px;
  }

  .info-card {
    margin: 0 0 0 10px;
    width: 50%;

    a {
      text-decoration: underline;
      color: rgb(0, 89, 255);
    }
  }

  .outer-grid {
    display: grid;
    grid-template-rows: 1fr;
    grid-template-columns: 25% 75%;
    grid-auto-flow: column;
    padding: 10px;
    border-radius: 20px;
    width: 100%;

    .isCurrentDay {
      background: rgba(214, 28, 28, 0.6);
    }
  }

  .inner-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 3fr;
    overflow-y: auto;

    .grid-tl-container {
      padding: 10px 5px;
      .holiday {
        transition: 0.5s;

        background: repeating-linear-gradient(
          45deg,
          var(--holiday-bg-color),
          var(--holiday-bg-color) 10px,
          rgba(0, 0, 0, 0) 10px,
          rgba(0, 0, 0, 0) 20px
        );
      }
      .weekend {
        transition: 0.5s;

        background-color: var(--weekend-bg-color);
        // height: 100%;
        // margin: 5px 0;
      }
      .datePassed {
        div,
        div > p {
          cursor: pointer;
        }
        transition: 0.5s;

        background-color: rgba(0, 0, 0, 0.25);
      }
    }

    .grid-scheduler-row {
      .holiday {
        transition: 0.5s;
        z-index: 1;

        background: repeating-linear-gradient(
          45deg,
          var(--holiday-bg-color),
          var(--holiday-bg-color) 10px,
          rgba(0, 0, 0, 0) 10px,
          rgba(0, 0, 0, 0) 20px
        );
      }
      // border-radius: 3rem;
      .weekend {
        transition: 0.5s;
        z-index: 1;

        background-color: var(--weekend-bg-color);
        // margin: 5px 0;
      }
      .datePassed {
        div,
        div > p {
          cursor: pointer;
        }
        transition: 0.5s;

        background-color: rgba(0, 0, 0, 0.199);
      }
    }
  }
}

.wrapper {
  overflow-y: auto;
  width: 100%;
  max-height: 150vh;
}
/* Event Info Cards */
.event-card-enter-active {
  transition: opacity 0.5s ease;
}
.event-card-leave-active {
  transition: opacity 0.5s ease;
}
.event-card-enter-from,
.event-card-leave-to {
  transform: translateX(20px);
}
/* End Event Info Cards */
.grid-scheduler-row {
  display: grid;
  height: var(--row-height);
  margin: 0 5px 10px;
  grid-auto-columns: var(--column-width);
  border-radius: 50px;
  overflow: hidden;
}
.body-cell {
  transition: border 0.5s;

  grid-row: 1;
  border-left: 2px solid var(--border-color);

  grid-event {
    border-radius: 15px;
    overflow: hidden;
  }

  &:last-child {
    border-left: 2px solid var(--border-color);
  }
  &:first-child {
    border-left: none;
  }

  grid-leave-period {
    border-radius: 15px;
    overflow: hidden;
  }

  grid-task {
    border-radius: 15px;
    overflow: hidden;
  }

  grid-intervention-followed {
    border-radius: 15px;
    overflow: hidden;
  }
}

.grid-scheduler-container {
  //scroll
  height: 25rem;
  overflow-y: scroll;
  overflow-x: hidden;
  padding: 5px 0;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
}
.grid-users-container {
  margin-top: 5.55px;
  display: flex;
  flex-direction: column;
  z-index: 2;
  padding: 0 17px 10px 0;

  //scroll
  height: 25rem;
  overflow-y: scroll;
  overflow-x: hidden;
}
.grid-container {
  overflow: auto;
  width: 100%;
  height: 95%;
  padding: 10px 20px;

  ::-webkit-scrollbar {
    height: 5px;
    width: 5px;
  }

  ::-webkit-scrollbar-track {
    background: var(--main-bg-color);
    border-radius: 5px;
  }
  ::-webkit-scrollbar-thumb {
    background: rgb(109, 109, 109);
    height: 10px;
    width: 10px;
  }
}

.add-event-btn {
  display: flex;
  margin: 0 0 20px 0;
  align-items: center;

  .text {
    margin: 5px 0 0 10px;
  }
}

.grid-tl-container {
  border-radius: 10px;
  top: 0;
  display: grid;
  grid-auto-columns: var(--column-width);
  z-index: 2;
}
.grid-tl-container > span {
  align-content: center;
}
.grid-header {
  font-size: 0.9rem;
  text-align: center;
  margin: 5px;
}
.grid-month-name {
  grid-area: month-area;
  grid-row: 1;
  padding-left: 5px;
  border-radius: 30px;
  height: 21px;
  overflow: hidden;
}
.week-cell {
  grid-row: 2;
  padding-left: 5px;
  border-radius: 30px;
}
.day-cell {
  text-align: center;
  grid-row: 3;
  border-radius: 30px;
}
.date-cell {
  text-align: center;
  grid-row: 4;
  border-radius: 30px;
}
.calendarSelection {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

#selectTimeCalendar {
  margin: 10px;
}
.main-filters {
  margin: 10px auto 20px;
  border-radius: 25px;
}

.pickerTimeDirection {
  position: absolute;
  z-index: 3;
  margin-top: 22rem;
}
.add-event {
  padding: 10px;
}

.blank-area {
  margin-bottom: 5px;
  overflow-y: scroll;
}

.timeSelector {
  display: flex;
  justify-content: center;
  align-items: center;

  span {
    padding: 0 10px;
  }

  .bordered {
    border-right: 2px solid var(--border-color);
  }
}

.currentTime {
  padding: 0 1rem;
  font-weight: bold;
}

.notificationMessage {
  position: absolute;
  z-index: 4;
  top: 27%;
  left: 2%;
}
</style>
